import { useMutation, useQuery } from "@apollo/client";
import {
    getIssue,
    getIssueHistoricChangesLog,
    getIssues,
    getIssuesCount,
    getKeyPerformanceIndicators,
    getKeySuccessFactors,
    insertIssue,
    insertObservationIssue,
    updateIssue,
    updateIssueMedia,
    updateIssueWithKsf,
} from "api/queries/RCA/IssueAPI";
import { IHistoryLogIssue, IIssue, IIssueEntity, IIssuesTableOperationFilters } from "./IIssueEntityInterfaces";
import { useEffect, useMemo, useState } from "react";
import { notSelected, RCA_STATUSES } from "constants/constants";
import { IssuesColumnsIdEnum } from "pages/Corporate/RCA/RCAIssues/useRCAIssuesColumns/useRCAIssuesColumnsInterfaces";
import { IKeyPerformanceIndicator, IKeySuccessFactor } from "../RCALibraryEntity/RCALibraryEntityInterfaces";
import { IMyIssuesFilters } from "pages/Corporate/RCA/RCADashboard/components/MyIssues/MyIssuesInterfaces";

export const IssueEntity: IIssueEntity = {
    InsertIssue: () => {
        const [createIssue, { error }] = useMutation(insertIssue);
        return { apolloMutationError: error, createIssue };
    },
    InsertObservationIssue: () => {
        const [createObservationIssue, { error }] = useMutation(insertObservationIssue);
        return { apolloMutationError: error, createObservationIssue };
    },
    GetIssues: (tableOperationFilters, filters) => {
        const [totalCount, setTotalCount] = useState<number>(0);
        const [issues, setIssues] = useState<IIssue[]>([]);

        const whereVariables = useMemo(
            () => ({
                mill_id: {
                    _in: filters.millsIds,
                },
                owner: {
                    user_id: {
                        _in: filters.ownerIds,
                    },
                },
                status: {
                    status_id: {
                        _in: filters.statusesIds,
                        ...(!filters.showCompleted
                            ? {
                                  _neq: RCA_STATUSES.COMPLETED,
                              }
                            : {
                                  _eq: RCA_STATUSES.COMPLETED,
                              }),
                    },
                },
                ...(filters?.meetingTypeIds &&
                    !filters.meetingTypeIds.includes(notSelected) &&
                    filters.totalMeetingTypes &&
                    filters.meetingTypeIds.length !== filters.totalMeetingTypes && {
                        meeting_type_id: { _in: filters.meetingTypeIds },
                    }),
                ...(filters?.meetingTypeIds &&
                    filters.meetingTypeIds.includes(notSelected) && {
                        meeting_type_id: { _is_null: true },
                    }),
                ...(filters.searchText && {
                    _or: [
                        { title: { _ilike: `%${filters.searchText}%` } },
                        { description: { _ilike: `%${filters.searchText}%` } },
                        ...(Number.isInteger(parseInt(filters.searchText))
                            ? [{ issue_id: { _eq: parseInt(filters.searchText) } }]
                            : []),
                        { owner: { first_name: { _ilike: `%${filters.searchText}%` } } },
                        { owner: { last_name: { _ilike: `%${filters.searchText}%` } } },
                    ],
                }),
            }),
            [filters],
        );

        useQuery(getIssuesCount, {
            fetchPolicy: "no-cache",
            onCompleted: (data) => {
                if (data?.issue_aggregate) {
                    const totalCount: number = data?.issue_aggregate?.aggregate?.count || 0;
                    setTotalCount(totalCount);
                    if (totalCount < 1) {
                        setIssues([]);
                    }
                }
            },
            variables: {
                where: whereVariables,
            },
        });

        const { error, loading, refetch } = useQuery(getIssues, {
            fetchPolicy: "no-cache",
            skip: !totalCount,
            onCompleted: (data) => {
                if (data?.issue) {
                    const issues: IIssue[] = data.issue;
                    setIssues(issues);
                }
            },
            variables: {
                limit: tableOperationFilters.limit,
                offset: tableOperationFilters.offset,
                order_by: {
                    ...(tableOperationFilters.orderBy === IssuesColumnsIdEnum.NUMBER && {
                        issue_id: tableOperationFilters.order,
                    }),
                    ...(tableOperationFilters.orderBy === IssuesColumnsIdEnum.DESCRIPTION && {
                        description: tableOperationFilters.order,
                    }),
                    ...(tableOperationFilters.orderBy === IssuesColumnsIdEnum.DUE_DATE && {
                        due_date: tableOperationFilters.order,
                    }),
                    ...(tableOperationFilters.orderBy === IssuesColumnsIdEnum.STATUS && {
                        status: {
                            order: tableOperationFilters.order,
                        },
                    }),
                    ...(tableOperationFilters.orderBy === IssuesColumnsIdEnum.OWNER && {
                        owner: {
                            user_id: tableOperationFilters.order,
                        },
                    }),
                },
                where: whereVariables,
            },
        });

        return { apolloQueryError: error, issues, loading, refetch, totalCount };
    },
    GetIssue: (issueId: number) => {
        const { data, error, loading, refetch } = useQuery(getIssue, {
            fetchPolicy: "no-cache",
            variables: { issue_id: issueId },
        });

        const [issue, setIssue] = useState<IIssue>({} as IIssue);

        useEffect(() => {
            if (data) {
                setIssue(data.issue[0]);
            }
        }, [data]);

        return { apolloQueryError: error, issue, loading, refetch };
    },
    UpdateIssue: () => {
        const [updateIssueMutation, { error }] = useMutation(updateIssue);
        return { apolloMutationError: error, updateIssueMutation };
    },
    UpdateIssueWithKsf: () => {
        const [updateIssueWithKsfMutation, { error }] = useMutation(updateIssueWithKsf);
        return { apolloMutationError: error, updateIssueWithKsfMutation };
    },
    GetUserIssues: (tableOperationFilters: IIssuesTableOperationFilters, userId: number, filters: IMyIssuesFilters) => {
        const [totalCount, setTotalCount] = useState<number>(0);
        const [userIssues, setUserIssues] = useState<IIssue[]>([]);

        const whereVariables = useMemo(
            () => ({
                owner: {
                    user_id: {
                        _eq: userId,
                    },
                },
                status: {
                    status_id: {
                        ...(!filters.showCompleted
                            ? { _neq: RCA_STATUSES.COMPLETED }
                            : { _eq: RCA_STATUSES.COMPLETED }),
                    },
                },
            }),
            [filters, userId],
        );

        useQuery(getIssuesCount, {
            fetchPolicy: "no-cache",
            onCompleted: (data) => {
                if (data?.issue_aggregate) {
                    const totalCount: number = data?.issue_aggregate?.aggregate?.count || 0;
                    setTotalCount(totalCount);
                    if (totalCount < 1) {
                        setUserIssues([]);
                    }
                }
            },
            variables: {
                where: whereVariables,
            },
        });

        const { error, loading, refetch } = useQuery(getIssues, {
            fetchPolicy: "no-cache",
            skip: !totalCount,
            onCompleted: (data) => {
                if (data?.issue) {
                    const issues: IIssue[] = data.issue;
                    setUserIssues(issues);
                }
            },
            variables: {
                limit: tableOperationFilters.limit,
                offset: tableOperationFilters.offset,
                order_by: {
                    ...(tableOperationFilters.orderBy === IssuesColumnsIdEnum.NUMBER && {
                        issue_id: tableOperationFilters.order,
                    }),
                    ...(tableOperationFilters.orderBy === IssuesColumnsIdEnum.DESCRIPTION && {
                        description: tableOperationFilters.order,
                    }),
                    ...(tableOperationFilters.orderBy === IssuesColumnsIdEnum.DUE_DATE && {
                        due_date: tableOperationFilters.order,
                    }),
                    ...(tableOperationFilters.orderBy === IssuesColumnsIdEnum.STATUS && {
                        status: {
                            order: tableOperationFilters.order,
                        },
                    }),
                },
                where: whereVariables,
            },
        });

        return { apolloQueryError: error, userIssues, loading, refetch, totalCount };
    },
    GetIssueHistoricChangesLog: (issueId?: number) => {
        const [issueLogs, setIssueLogs] = useState<IHistoryLogIssue[]>([]);

        const { error, loading, refetch } = useQuery(getIssueHistoricChangesLog, {
            skip: !issueId,
            onCompleted: (data) => {
                if (data?.issue_history_log) {
                    const issueLogsData: IHistoryLogIssue[] = data?.issue_history_log;
                    setIssueLogs(issueLogsData);
                }
            },
            variables: {
                id: issueId,
            },
        });

        return { apolloQueryError: error, issueLogs, loading, refetch };
    },
    GetKeySuccessFactors: () => {
        const { data, error, loading, refetch } = useQuery(getKeySuccessFactors);
        const [keySuccessFactors, setKeySuccessFactors] = useState<IKeySuccessFactor[]>([]);
        useEffect(() => {
            if (data) {
                setKeySuccessFactors(data.key_success_factors);
            }
        }, [data]);
        return { apolloQueryError: error, keySuccessFactors, loading, refetch };
    },
    GetKeyPerformanceIndicators: () => {
        const { data, error, loading, refetch } = useQuery(getKeyPerformanceIndicators);
        const [keyPerformanceIndicators, setKeyPerformanceIndicators] = useState<IKeyPerformanceIndicator[]>([]);
        useEffect(() => {
            if (data) {
                setKeyPerformanceIndicators(data.key_performance_indicators);
            }
        }, [data]);
        return { apolloQueryError: error, keyPerformanceIndicators, loading, refetch };
    },
    UpdateIssueMedia: () => {
        const [updateIssueMediaMutation, { error }] = useMutation(updateIssueMedia);
        return { apolloMutationError: error, updateIssueMediaMutation };
    },
};
